import React, {
  createContext,
  MouseEvent,
  useContext,
  useEffect,
  useRef
} from 'react'
import * as ReactDom from 'react-dom'
import {Util} from 'yougile-util'
import {Overlay} from '@components/overlay'
import {useBoolean, useGlobalKeyDown} from '@containers/hooks'
import Tippy, {TippyProps} from '@tippyjs/react'

export interface DropdownProps
  extends Pick<
    TippyProps,
    | 'animation'
    | 'className'
    | 'reference'
    | 'arrow'
    | 'trigger'
    | 'hideOnClick'
    | 'offset'
    | 'maxWidth'
  > {
  onVisibleChange?: (visible: boolean) => void // todo: consider name "onClose"
  visible?: boolean // todo: consider name "isOpen"
  contentState?: unknown
  hideArrow?: boolean
  content: React.ReactNode
  duration?: number
  placement?: TippyProps['placement']
  fallbackPlacements?: TippyProps['placement'][]
  children?: React.ReactNode
  theme?: 'light-border' | 'new-tooltips' | 'new-menu'
  hideOverlay?: boolean
  instanceRef?: React.MutableRefObject<TippyInstance | undefined>
  onCreate?: (tippy: TippyInstance) => void
  /**
   * Если true, то дропдаун будет автоматически пересчитывать позицию при изменении размера контента
   * @default false
   */
  autoRelayout?: boolean
}

export interface TippyInstance {
  hide: () => void
  popperInstance?: {update: () => void}
}

type RelayoutTrigger = () => void
export const DropdownRelayoutTriggerContext = createContext<RelayoutTrigger>(
  Util.noop
)
const OverlayZIndexContext = createContext(1000)

export const Dropdown = ({
  content,
  hideArrow,
  contentState,
  placement = 'bottom-start',
  duration = 100,
  fallbackPlacements = ['top-start', 'auto'],
  children,
  onVisibleChange,
  theme = 'light-border',
  hideOverlay,
  offset,
  onCreate,
  autoRelayout = false,
  ...rest
}: DropdownProps) => {
  const mount = useBoolean(false)
  const visibleRef = useRef(false)
  const instanceRef = useRef<TippyInstance>()
  const contentRef = useRef<HTMLDivElement>()
  const hide = (): boolean | void => {
    const visible = visibleRef.current
    // todo: consider remove uncontrolled mode (special handling for undefined)
    if (rest.visible !== undefined) {
      onVisibleChange?.(false)
      return visible
    } else {
      if (instanceRef.current) {
        instanceRef.current.hide()
        return visible
      }
    }
  }
  const onOverlayClick = (e: MouseEvent) => {
    e.stopPropagation()
    hide()
  }
  useGlobalKeyDown({
    escape: hide
  })
  useEffect(() => {
    if (contentState !== undefined) {
      instanceRef.current?.popperInstance?.update()
    }
  }, [contentState])
  useEffect(() => {
    if (!contentRef.current || !autoRelayout) {
      return undefined
    }

    const resizeObserver = new ResizeObserver(() => {
      instanceRef.current?.popperInstance?.update()
    })

    resizeObserver.observe(contentRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [mount.value, autoRelayout])
  const relayoutTrigger = () => {
    instanceRef.current?.popperInstance?.update()
  }
  const overlayZIndex = useContext(OverlayZIndexContext)
  visibleRef.current = mount.value

  const onCreateMenu = (tippy: TippyInstance) => {
    instanceRef.current = tippy
    onCreate?.(tippy)
  }

  return (
    <>
      <DropdownRelayoutTriggerContext.Provider value={relayoutTrigger}>
        <OverlayZIndexContext.Provider value={overlayZIndex + 20}>
          <Tippy
            theme={theme}
            placement={placement}
            interactive={true}
            maxWidth={500}
            offset={offset ?? [0, 4]}
            zIndex={overlayZIndex + 10}
            arrow={!hideArrow}
            onCreate={x => onCreateMenu(x as TippyInstance)}
            appendTo={document.body}
            trigger={rest.visible === undefined ? 'click' : undefined}
            duration={duration}
            content={
              mount.value ? (
                autoRelayout ? (
                  <div ref={contentRef}>{content}</div>
                ) : (
                  content
                )
              ) : null
            }
            onShow={() => mount.turnOn()}
            onHidden={() => mount.turnOff()}
            popperOptions={{
              modifiers: [{name: 'flip', options: {fallbackPlacements}}]
            }}
            {...rest}
          >
            {children as React.ReactElement}
          </Tippy>
        </OverlayZIndexContext.Provider>
      </DropdownRelayoutTriggerContext.Provider>
      {!hideOverlay &&
        mount.value &&
        ReactDom.createPortal(
          <Overlay style={{zIndex: overlayZIndex}} onClick={onOverlayClick} />,
          document.body
        )}
    </>
  )
}
